import { Popover, Progress, Tag, Tooltip, message } from "antd";
import { Trans } from "react-i18next";
import { IconStepReload } from "@icons/index";
import { IconLineExport } from "@kalodata/kds-design-icons";
import Image from "@components/Image";
import CostPopover from "@components/CostPopover";
import VideoCover from "@layouts/VideoCover";
import { GeneratedDetailExportMenu } from "@layouts/GlobalVideoDetailModal/GeneratedDetailExportMenu";
import { FocusAction } from "./FocusAction";
import ReportIssueModal from "./ReportIssueModal";
import { statusConfig, markCardAsViewed, isCardViewed, updateViewedTimestamp } from "../librarySlice";
import { getAssetCoverUrl } from "../hooks/useGeneratedVideoAssets";
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Checkbox } from "@kalodata/kds-design";
import { useHover } from "ahooks";
import { useAppDispatch } from "@hooks/redux";
import { resubmitAiJob } from "@apis/http/api";
import { t } from "i18next";
import classNames from "classnames";
import MemberPopoverContent from "@components/MemberPopoverContent";
import { useVideoGenerateCard } from "./hooks";
import { useVideoJobStatusPoll } from "./useVideoJobStatusPoll";
import { fetchAiCredits } from "@store/globalSlice";
import { themeAtom } from "@src/jotai/theme";
import { useAtomValue } from "jotai";
import { LeftTopCorner } from "./LeftTopCorner";
import type { UserAssetV2Record } from "@apis/http/api";
import { UserAiJobItemResponseStatus, VidExtraVideoPublishStatus } from "@apis/generated/openapi";
import { NewVideoTag } from "./NewVideoTag";
import { twMerge } from "tailwind-merge";
import { TestId } from "@shared/testIds";

const DOTS_PER_ROW = 10;

const DOT_RADIUS_MIN = 1;
const DOT_RADIUS_MAX = 1.44;
const SPACING_REF_MIN = 20;
const SPACING_REF_MAX = 40;

interface DotMatrixCanvasProps {
  speedMin?: number;
  speedMax?: number;
}

const DotMatrixCanvas = ({ speedMin = 1.2, speedMax = 2.8 }: DotMatrixCanvasProps) => {
  const isDark = useAtomValue(themeAtom) === "dark";
  const opacityRef = useRef({ min: 0.5, max: 1 });
  opacityRef.current = isDark ? { min: 0.08, max: 0.3 } : { min: 0.5, max: 1 };

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number>(0);
  const dotsRef = useRef<{ phase: number; speed: number }[]>([]);
  const sizeRef = useRef({ w: 0, h: 0, cols: 0, rows: 0, spacing: 0, radius: DOT_RADIUS_MAX });

  const initDots = useCallback(
    (cols: number, rows: number) => {
      const count = cols * rows;
      const dots: { phase: number; speed: number }[] = [];
      for (let i = 0; i < count; i++) {
        dots.push({
          phase: Math.random() * Math.PI * 2,
          speed: speedMin + Math.random() * (speedMax - speedMin),
        });
      }
      dotsRef.current = dots;
    },
    [speedMin, speedMax],
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const observer = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const dotSpacing = Math.min(width, height) / (DOTS_PER_ROW + 1);
      const t = Math.min(1, Math.max(0, (dotSpacing - SPACING_REF_MIN) / (SPACING_REF_MAX - SPACING_REF_MIN)));
      const radius = DOT_RADIUS_MIN + t * (DOT_RADIUS_MAX - DOT_RADIUS_MIN);
      const cols = Math.floor(width / dotSpacing) + 1;
      const rows = Math.floor(height / dotSpacing) + 1;
      const prev = sizeRef.current;
      if (cols !== prev.cols || rows !== prev.rows) {
        initDots(cols, rows);
      }
      sizeRef.current = { w: width, h: height, cols, rows, spacing: dotSpacing, radius };
    });

    observer.observe(canvas);

    let startTime = performance.now();
    const draw = (now: number) => {
      const elapsed = (now - startTime) / 1000;
      const { w, h, cols, rows, spacing, radius } = sizeRef.current;
      if (!spacing) {
        rafRef.current = requestAnimationFrame(draw);
        return;
      }
      const dots = dotsRef.current;

      ctx.clearRect(0, 0, w, h);

      const offsetX = (w - (cols - 1) * spacing) / 2;
      const offsetY = (h - (rows - 1) * spacing) / 2;

      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const idx = r * cols + c;
          const dot = dots[idx];
          if (!dot) continue;

          const x = offsetX + c * spacing;
          const y = offsetY + r * spacing;

          const sin1 = Math.sin(elapsed * dot.speed + dot.phase);
          const sin2 = Math.sin(elapsed * dot.speed * 0.6 + dot.phase * 1.7);
          const raw = (sin1 + sin2) / 2;
          const { min: minOpacity, max: maxOpacity } = opacityRef.current;
          const opacity = minOpacity + ((raw + 1) / 2) * (maxOpacity - minOpacity);

          ctx.beginPath();
          ctx.arc(x, y, radius, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(255,255,255,${opacity})`;
          ctx.fill();
        }
      }

      rafRef.current = requestAnimationFrame(draw);
    };

    rafRef.current = requestAnimationFrame(draw);

    return () => {
      observer.disconnect();
      cancelAnimationFrame(rafRef.current);
    };
  }, [initDots]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute z-1"
      style={{ top: "-5%", left: "-5%", width: "110%", height: "110%" }}
    />
  );
};

export const RotatingGradientBg = ({ showDot = true, ...dotProps }: { showDot?: boolean } & DotMatrixCanvasProps) => (
  <>
    <div className="rotating-bg" />
    {showDot && <DotMatrixCanvas {...dotProps} />}
  </>
);

interface UIVideoGenerateCardProps {
  record: UserAssetV2Record;
  onDownload?: (record: UserAssetV2Record) => void;
  onDetailClick?: (record: UserAssetV2Record) => void;
  onRefresh?: () => void;
  selected?: boolean;
  onSelectedChange?: (selected: boolean) => void;
}

const UIVideoGenerateCard = ({
  record,
  onDetailClick,
  onRefresh,
  selected = false,
  onSelectedChange,
}: UIVideoGenerateCardProps) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const isHovered = useHover(cardRef);
  const showCheckbox = selected || isHovered;
  const recordStatus = record?.jobItem?.status;
  const baseIsProcessing =
    recordStatus === UserAiJobItemResponseStatus.processing || recordStatus === UserAiJobItemResponseStatus.queued;
  // 卡片自身处于进行中时启用单卡片轮询，命中终态后用轮询结果覆盖列表里的初始 record
  const live = useVideoJobStatusPoll(record?.jobItem?.jobId, baseIsProcessing);
  const status = live?.status ?? recordStatus;
  const errorMessage = live?.errorMessage ?? record?.jobItem?.errorMessage;
  const internalModelId = record?.jobItem?.internalModelId;
  const hasInternalModelId = !!internalModelId;
  const context = record?.jobItem?.context || {};
  const config = statusConfig[status || UserAiJobItemResponseStatus.queued];
  const [showNew, setShowNew] = useState(false);
  const dispatch = useAppDispatch();
  const { retryCost, totalRemain, isBasicUp, handleRetryHoverChange, handleVideoRecreation } = useVideoGenerateCard(
    record,
    onRefresh,
  );
  const [reportOpen, setReportOpen] = useState(false);
  const isDark = useAtomValue(themeAtom) === "dark";

  const [retryLoading, setRetryLoading] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);

  const isProcessing =
    status === UserAiJobItemResponseStatus.processing || status === UserAiJobItemResponseStatus.queued;
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    if (!isProcessing) return;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [isProcessing]);

  const progress = useMemo(() => {
    if (!record.gmtCreated || !record.gmtEstimatedCompleted) return 0;
    const start = new Date(record.gmtCreated).getTime();
    const end = new Date(record.gmtEstimatedCompleted).getTime();
    const total = end - start;
    if (total <= 0) return 95;
    const elapsed = now - start;
    return Math.min(95, Math.max(0, Math.round((elapsed / total) * 100)));
  }, [record.gmtCreated, record.gmtEstimatedCompleted, now]);

  const isOverdue = useMemo(() => {
    if (!record.gmtCreated) return false;
    const start = new Date(record.gmtCreated).getTime();
    return now - start > 10 * 60 * 1000;
  }, [record.gmtCreated, now]);

  // 初始化时检查是否已查看
  useEffect(() => {
    if (record.id) {
      setShowNew(!isCardViewed(record.id));
    }
  }, [record.id]);

  const handleCardClick = () => {
    if (record.id && showNew) {
      markCardAsViewed(record.id);
      setShowNew(false);
      dispatch(updateViewedTimestamp());
    }
    if (onDetailClick) {
      onDetailClick(record);
    }
  };

  const handleVideoResubmit = async (jobId: string | number | undefined) => {
    if (!jobId || retryLoading) return message.error(t("Failed"));
    setRetryLoading(true);
    try {
      await resubmitAiJob({
        id: String(jobId),
        internalModelId: internalModelId || "",
      } as any);
      message.success(t("Submitted successfully"));
      onRefresh?.();
      dispatch(fetchAiCredits());
    } catch {
      message.error(t("Failed"));
    } finally {
      setRetryLoading(false);
    }
  };

  // 重新生成按钮逻辑
  const getPopoverContent = () => {
    if (!isBasicUp) return <MemberPopoverContent desc="Upgrade to use the feature" action="Upgrade" />;
    if (retryCost !== null && totalRemain < retryCost)
      return <MemberPopoverContent remainCredit={totalRemain} action="Credits" />;
    return null;
  };

  const renderRetryBtn = () => {
    const button = (
      <span>
        <IconStepReload
          className="w-[16px] h-[16px] cursor-pointer text-kds-fg-subtle hover:text-kds-fg transition-colors"
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            handleVideoResubmit(record?.jobItem?.jobId);
          }}
        />
      </span>
    );

    const popoverContent = getPopoverContent();
    if (popoverContent) {
      return (
        <Popover content={popoverContent} trigger="hover">
          {button}
        </Popover>
      );
    }

    return (
      <CostPopover text={t("Retry")} cost={retryCost || "-"} onOpenChange={handleRetryHoverChange}>
        {button}
      </CostPopover>
    );
  };

  const renderAnotherBtn = () => {
    if (!hasInternalModelId) return null;
    const button = (
      <span>
        <IconStepReload
          className="w-[16px] h-[16px] cursor-pointer text-kds-fg-subtle hover:text-kds-fg transition-colors"
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            handleVideoRecreation();
          }}
        />
      </span>
    );

    const popoverContent = getPopoverContent();
    if (popoverContent) {
      return (
        <Popover content={popoverContent} trigger="hover">
          {button}
        </Popover>
      );
    }

    return (
      <CostPopover text={t("Generate Another")} cost={retryCost || "-"} onOpenChange={handleRetryHoverChange}>
        {button}
      </CostPopover>
    );
  };

  // 渲染封面区域
  const renderCover = () => {
    const coverImageUrl = live?.coverImageUrl ?? getAssetCoverUrl(record);
    const videoUrl = live?.videoUrl ?? (context as any)?.videoUrl;

    if (status === "processing") {
      return (
        <div className="w-full h-full flex flex-col items-center justify-center gap-[8px] generate-card-item">
          <RotatingGradientBg />
          <span className="relative z-2 text-[16px] font-bold text-kds-fg">{progress}%</span>
          <Progress
            percent={progress}
            showInfo={false}
            strokeColor={isDark ? { from: "#71717A", to: "white" } : { from: "#0191F8", to: "#8466FD" }}
            className="w-[120px]! relative z-2"
          />

          {isOverdue && (
            <span className="relative z-2 text-[14px] text-kds-fg-subtle">
              <Trans>Brewing your video...</Trans>
            </span>
          )}
        </div>
      );
    }

    if (status === UserAiJobItemResponseStatus.queued) {
      return (
        <div className="w-full h-full flex items-center justify-center generate-card-item">
          <RotatingGradientBg showDot={false} />
          <span className="relative z-2 text-[16px] text-kds-fg-soft">
            <Trans>Queued</Trans>
          </span>
        </div>
      );
    }

    return (
      <div className="relative">
        <VideoCover
          cover={coverImageUrl}
          className={classNames(
            "w-full aspect-3/4 border-none! rounded-none!",
            (context as any).aspectRatio === "RATIO_16_9" && "bg-contain!",
          )}
          videoUrl={onDetailClick ? undefined : videoUrl}
          aspectRatio={(context as any).aspectRatio}
          showAdIcon={false}
          videoIconSize="size64"
          adIconSize="size24"
          videoType="video"
        ></VideoCover>
      </div>
    );
  };

  return (
    <div
      ref={cardRef}
      data-testid={TestId.LibraryGeneratedVideoCard}
      data-status={status}
      className="kalo-white-card flex flex-col w-full group relative p-0! overflow-hidden cursor-pointer"
      onClick={handleCardClick}
    >
      <Checkbox
        checked={selected}
        onCheckedChange={(checked) => onSelectedChange?.(checked === true)}
        className={twMerge(
          "absolute left-[12px] top-[12px] z-[10]",
          "size-4 shrink-0 rounded-[4px] shadow-sm",
          "border-0 bg-kds-mask",
          "data-[state=checked]:border-kds-accent data-[state=checked]:bg-kds-accent data-[state=checked]:text-kds-light",
          "data-[state=checked]:**:data-[slot=checkbox-indicator]:grid!",
          "data-[state=checked]:[&_[data-slot=checkbox-indicator]_svg]:size-3",
          "cursor-pointer disabled:cursor-not-allowed disabled:opacity-40",
          showCheckbox ? "inline-flex" : "hidden",
        )}
        onClick={(event) => event.stopPropagation()}
        onPointerDown={(event) => event.stopPropagation()}
      />
      <div className="relative w-full aspect-3/4">
        <NewVideoTag
          isNew={
            showNew &&
            record.vidExtra?.videoPublishStatus !== VidExtraVideoPublishStatus.PENDING_PUBLISH &&
            record.vidExtra?.videoPublishStatus !== VidExtraVideoPublishStatus.SUCCESS
          }
        />
        {renderCover()}
      </div>

      <div className="p-[16px] border-t border-kds-border">
        {/* 商品信息 */}
        <div className="pb-[12px] flex items-start gap-[8px] border-b border-kds-border">
          {(context as any).productCoverImageUrl ? (
            <Image className="w-[32px] h-[32px] shrink-0 rounded-[4px]" url={(context as any).productCoverImageUrl} />
          ) : (
            <div className="w-[32px] h-[32px] shrink-0 rounded-[4px] bg-kds-bg-soft border border-kds-border" />
          )}
          <div className="flex-1 min-w-0">
            <p className="text-[13px] text-kds-fg-subtle leading-[18px] line-clamp-2 m-0">
              {(context as any).productTitle || "-"}
            </p>
          </div>
        </div>

        {/* 状态栏 */}
        <div className="flex items-center justify-between pt-[12px]">
          <Tooltip title={t(errorMessage || "")} open={errorMessage ? undefined : false} placement="top">
            <Tag variant="filled" color={config.color} className="rounded-full!">
              <Trans>{config.label}</Trans>
            </Tag>
          </Tooltip>
          {hasInternalModelId && (
            <div className="flex items-center gap-[8px]" onClick={(e) => e.stopPropagation()}>
              {/* 重新生成按钮 */}
              {status === "failed" ? renderRetryBtn() : renderAnotherBtn()}
            </div>
          )}
        </div>
      </div>

      {![
        UserAiJobItemResponseStatus.processing,
        UserAiJobItemResponseStatus.queued,
        UserAiJobItemResponseStatus.failed,
      ].includes(status || UserAiJobItemResponseStatus.queued) && (
        <>
          <LeftTopCorner record={record} />
          {/* 导出菜单 */}
          <div
            className={classNames("absolute right-[42px] top-[11px] group-hover:flex", exportOpen ? "flex" : "hidden")}
            onClick={(e) => e.stopPropagation()}
          >
            <GeneratedDetailExportMenu
              assetId={Number(record.id)}
              disabled={!(context as any).videoUrl}
              download={{
                videoURL: (context as any).videoUrl,
                videoId: String(record.id),
                videoSource: (record as any).videoSource || "",
                kalo_type: "generated_card",
              }}
              popoverTrigger="hover"
              popoverPlacement="bottomRight"
              open={exportOpen}
              onOpenChange={setExportOpen}
              renderTrigger={({ disabled }) => (
                <button
                  type="button"
                  data-testid={TestId.LibraryGeneratedVideoExport}
                  disabled={disabled}
                  className={twMerge(
                    "w-[24px] h-[24px] cursor-pointer flex items-center justify-center rounded-[4px] border-0 p-0 disabled:cursor-not-allowed disabled:opacity-50 bg-kds-mask",
                  )}
                >
                  <IconLineExport size={16} className="text-white" />
                </button>
              )}
            />
          </div>
          {/* 点点点 */}
          <div onClick={(e) => e.stopPropagation()}>
            <FocusAction
              className="absolute right-[11px] top-[11px] hidden group-hover:flex"
              id={record.id}
              type="video-generation"
              showReportIssus
              onReportIssue={status === "completed" ? () => setReportOpen(true) : undefined}
              onComplete={onRefresh}
            />
          </div>
        </>
      )}

      <div onClick={(e) => e.stopPropagation()}>
        <ReportIssueModal
          open={reportOpen}
          onClose={() => setReportOpen(false)}
          assetId={Number(record?.id)}
          jobType="VIDEO_CREATION"
          onSubmitted={onRefresh}
        />
      </div>
    </div>
  );
};

export default UIVideoGenerateCard;
